import React, { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Link, useHistory } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import { Badge, Button, Card, CardBody, CardHeader, Col, Progress, Row, Table } from 'reactstrap'
import { useFetch } from '../../hooks'
import { api } from '../../utils'
import Header from '../../components/Headers/Header'
import { all, create } from 'mathjs'
import { notify } from '../../utils/notify'
import ClienteSelect from '../../components/molecules/ClienteSelect'
import { buildCategoryTextGenQueryParams, getCategoryTextGenClientName } from './listingUtils'


const math = create(all)

const CategoryTextGenListing = () => {
  const history = useHistory()
  const itemPerPage = 10

  const [pages, setPages] = useState(0)
  const [tableData, setTableData] = useState([])
  const [actualPage, setActualPage] = useState(1)
  const [selectedClientId, setSelectedClientId] = useState('')
  const [queryParams, setQueryParams] = useState(() =>
    buildCategoryTextGenQueryParams({ page: 1, pageSize: itemPerPage })
  )

  const { data, info, loading } = useFetch({
    url: 'category-text-gen/',
    params: { page_size: itemPerPage },
  })
  const { data: clienti } = useFetch({ url: 'clienti/' })

  useEffect(() => {
    if (data.length > 0 && tableData.length === 0) {
      setTableData(data)
      setPages(Math.ceil(info.count / itemPerPage))
    }
  }, [data, info, tableData])

  const fetchCategoryGenJobs = (params) => {
    api
      .get('category-text-gen/', {
        params,
      })
      .then((response) => {
        setTableData(response.data.results)
        setPages(Math.ceil(response.data.count / itemPerPage))
      })
      .catch((error) => console.error(error))
  }

  const handlePageClick = (data) => {
    const currentPage = data.selected + 1
    setActualPage(currentPage)
    const nextParams = {
      ...queryParams,
      page: currentPage,
      page_size: itemPerPage,
    }
    setQueryParams(nextParams)
    fetchCategoryGenJobs(nextParams)
  }

  const handleApplyFilters = () => {
    const nextParams = buildCategoryTextGenQueryParams({
      selectedClientId,
      page: 1,
      pageSize: itemPerPage,
    })

    setActualPage(1)
    setQueryParams(nextParams)
    fetchCategoryGenJobs(nextParams)
  }

  const handleClearFilters = () => {
    const nextParams = buildCategoryTextGenQueryParams({
      selectedClientId: '',
      page: 1,
      pageSize: itemPerPage,
    })

    setSelectedClientId('')
    setActualPage(1)
    setQueryParams(nextParams)
    fetchCategoryGenJobs(nextParams)
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'COMPLETED':
      case 'READY_TO_EXPORT':
      case 'DONE':
        return 'success'
      case 'ERROR':
        return 'danger'
      case 'IN_PROGRESS':
      case 'PENDING':
        return 'primary'
      default:
        return 'warning'
    }
  }

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo job?')) {
      return
    }

    try {
      await api.post(`category-text-gen/${id}/soft-delete/`)
      setTableData((prev) => prev.filter((item) => item.id !== id))
      notify('Job eliminato con successo.', 'success')
    } catch (error) {
      notify(error?.response?.data?.error || 'Impossibile eliminare il job.', 'error')
    }
  }

  return (
    <Header
      iconClass="fa-solid fa-file-lines text-white"
      title="Generatore Testi per Categorie"
      description={"Genera descrizioni ottimizzate per la SEO per le pagine di categoria, analizzando i prodotti presenti sulla pagina."}
    >
      {loading && <div className="text-center"><Spinner animation="grow" /></div>}
      {tableData && (
        <Row style={{ marginBottom: '100px' }}>
          <div className="col pb-5">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h1 className="mb-0">Job di Generazione Testi</h1>
                  </Col>
                  <Col className="text-right" xs="4">
                    <Button
                      color="info"
                      onClick={() => history.push('/add-category-text-gen/')}
                      size="md"
                    >
                      + Nuovo Job
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                <Row className="align-items-end">
                  <Col md={4}>
                    <ClienteSelect
                      id="category-text-gen-cliente-filter"
                      label="Cliente"
                      clienti={clienti || []}
                      selectedClientId={selectedClientId}
                      onChange={setSelectedClientId}
                      placeholder="Filtra per cliente"
                      isClearable
                    />
                  </Col>
                  <Col md={8} className="text-right pb-3">
                    <Button color="light" className="mr-2" onClick={handleClearFilters}>
                      Clear Filters
                    </Button>
                    <Button color="primary" onClick={handleApplyFilters}>
                      Apply
                    </Button>
                  </Col>
                </Row>
              </CardBody>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Nome</th>
                    <th scope="col">Cliente</th>
                    <th scope="col">Data</th>
                    <th scope="col">Progresso</th>
                    <th scope="col">Status</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item) => (
                    <tr key={item.id}>
                      <th scope="row">
                        <Link to={`/add-category-text-gen/${item.id}`}>{item.name}</Link>
                      </th>
                      <td>{getCategoryTextGenClientName(item)}</td>
                      <td>{new Date(item.created).toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div>
                            <Progress
                                max="100"
                                value={(math.evaluate(item.progress || '0/1') * 100).toString()}
                                barClassName={math.evaluate(item.progress || '0/1') === 1 ? "bg-success" : "bg-warning"}
                            />
                            {item.progress}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge color={getStatusBadge(item.status)} pill className="text-white">
                          {item.status}
                        </Badge>
                      </td>
                      <td className="text-right">
                        {math.evaluate(item.progress || '0/1') === 1 && (
                            <a
                                href={`${api.defaults.baseURL}category-text-gen/${item.id}/download_excel/`}
                                download
                            >
                                <Button color="success" size="sm" title="Download Excel"><i className="fas fa-file-download" /></Button>
                            </a>
                        )}
                        <Button
                          color="danger"
                          size="sm"
                          className="ml-2"
                          onClick={() => handleSoftDelete(item.id)}
                        >
                          Elimina
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                pageCount={pages}
                forcePage={actualPage - 1}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center pt-5 pb-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            </Card>
          </div>
        </Row>
      )}
    </Header>
  )
}

export default CategoryTextGenListing
